import { cn } from '@forest/shared'
import { AnimatePresence } from 'motion/react'
import { Dialog } from 'radix-ui'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { preload } from 'react-dom'
import { useTrialEligibility } from '~/hooks/use-trial-eligibility'
import { useContentTitle } from '~/hooks/use-content-title'
import {
  getFinalRewardPreviewSlides,
  withTitleSuffix,
  type FinalRewardPreviewSlide,
} from '~/lib/final-reward-preview-slides'
import { listenForSwipe } from '~/lib/swipe'
import { motion } from '~/lib/motion'
import { m } from '~/paraglide/messages'
import { $config } from '~/store/config'
import type { ItemContent } from '~/types/delivery'
import { CloseButton } from './close-button'
import { Spinner } from '@forest/shared/react'
import './final-reward-compare.css'

export function FinalRewardPreviewDialog(props: {
  reward: 'free' | 'paid'
  titleKey: string | null
  contentType: ItemContent['type'] | null
  isOpen: boolean
  isClaimed: boolean
  isClaimable: boolean
  isUnlocked: boolean
  onClaim: () => void
  onPurchase: () => void
  onClose: () => void
}) {
  const title = useContentTitle(props.titleKey)
  const { isTrial } = useTrialEligibility()
  const config = $config.get()!
  // Warm the slide images on page load so the dialog rarely waits on them.
  for (const src of [
    config.final_reward_compare_base_img,
    config.free_final_reward_compare_img,
    config.paid_final_reward_compare_img,
    config.free_final_reward_preview_slide_2_img_url,
    config.free_final_reward_preview_slide_3_img_url,
    config.paid_final_reward_preview_slide_2_img_url,
    config.paid_final_reward_preview_slide_3_img_url,
  ]) {
    if (src) preload(src, { as: 'image' })
  }
  return (
    <Dialog.Root
      open={props.isOpen}
      onOpenChange={(nextIsOpen) => !nextIsOpen && props.onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          asChild
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div
            className="gradient-outline fixed left-1/2 top-1/2 z-50 w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-[32px] outline-none backdrop-blur-md [--ow:2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            style={{
              ['--tw-enter-translate-x' as any]: '-50%',
              ['--tw-enter-translate-y' as any]: '-50%',
              ['--tw-exit-translate-x' as any]: '-50%',
              ['--tw-exit-translate-y' as any]: '-50%',
              willChange: 'backdrop-filter',
              background: '#FFFFFFCC',
            }}
          >
            <Dialog.Close asChild>
              <CloseButton className="absolute -right-2 -top-2" />
            </Dialog.Close>

            <div className="p-6 text-center">
              <SlidePager
                // Remount per reward so the pager and the compare's load
                // count and loop start over.
                key={props.reward}
                slides={getFinalRewardPreviewSlides(config, props.reward)}
                compareBaseSrc={config.final_reward_compare_base_img}
                compareRewardSrc={
                  props.reward === 'free'
                    ? config.free_final_reward_compare_img
                    : config.paid_final_reward_compare_img
                }
                title={title}
              >
                <Dialog.DialogDescription className="mt-3 text-[#666]">
                  {/* Only land skins have a generic description copy so far;
                      tree types wait on a what_is_tree_type Lokalise key. */}
                  {props.contentType === 'land_skin'
                    ? m.what_is_land_skin_description()
                    : ''}
                </Dialog.DialogDescription>
              </SlidePager>

              {!props.isClaimed &&
                (props.reward === 'paid' ? (
                  props.isClaimable || props.isUnlocked ? (
                    <button
                      className="mt-6 h-[52px] w-full rounded-full bg-white font-bold text-[#176371] disabled:opacity-30"
                      disabled={!props.isClaimable}
                      onClick={() => props.onClaim()}
                    >
                      {m.cart_claim_active()}
                    </button>
                  ) : (
                    <button
                      className="group mt-6 h-[52px] w-full rounded-full p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
                      onClick={() => props.onPurchase()}
                    >
                      <span className="flex h-full w-full items-center justify-center rounded-full font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
                        {isTrial ? m.btn_try_plus_free() : m.btn_unlock_plus()}
                      </span>
                    </button>
                  )
                ) : (
                  <button
                    className="mt-6 h-[52px] w-full rounded-full bg-white font-bold text-[#176371] disabled:opacity-30"
                    disabled={!props.isClaimable}
                    onClick={() => props.onClaim()}
                  >
                    {m.cart_claim_active()}
                  </button>
                ))}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/**
 * The banner pager (GLOSSARY.md "Final reward preview"), with the title that
 * carries the showing slide's suffix and the copy beneath it — everything
 * above the buttons is one swipe area. One slide per discrete swipe, no
 * wrap, dots as indicators only. The dialog content unmounts on close, so
 * every open lands on the first slide without sliding back.
 */
function SlidePager(props: {
  slides: FinalRewardPreviewSlide[]
  compareBaseSrc: string
  compareRewardSrc: string
  title: string
  children: ReactNode
}) {
  const [index, setIndex] = useState(0)
  // The strip animates only between swipes, never on the landing.
  const [hasSwiped, setHasSwiped] = useState(false)
  const swipeAreaRef = useRef<HTMLDivElement>(null)
  const lastIndex = props.slides.length - 1

  useEffect(
    () =>
      listenForSwipe(swipeAreaRef.current!, (direction) => {
        setHasSwiped(true)
        setIndex((current) =>
          Math.min(
            lastIndex,
            Math.max(0, current + (direction === 'left' ? 1 : -1)),
          ),
        )
      }),
    [lastIndex],
  )

  const showing = props.slides[index] ?? props.slides[0]

  return (
    // pan-y leaves vertical scrolling to the page.
    <div ref={swipeAreaRef} className="touch-pan-y">
      {/* Every slide image is a 300×200 export of the whole banner — the
          dialog's full width, its side padding included — so the strip
          bleeds through the padding to show it at 1×. */}
      <div className="-mx-6 h-[200px] overflow-hidden">
        <ul
          className={cn(
            'flex h-full',
            hasSwiped &&
              'transition-transform duration-300 ease-out motion-reduce:transition-none',
          )}
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {/* The slide list is fixed for the pager's life, so its position
              is its identity — two slides may share a URL. */}
          {props.slides.map((slide, slideIndex) => (
            <li key={slideIndex} className="h-full w-full shrink-0">
              {slide.kind === 'compare' ? (
                <FinalRewardCompare
                  baseSrc={props.compareBaseSrc}
                  rewardSrc={props.compareRewardSrc}
                />
              ) : (
                <PreviewImage src={slide.src} />
              )}
            </li>
          ))}
        </ul>
      </div>

      {props.slides.length > 1 && (
        <ol aria-hidden="true" className="mt-3 flex justify-center gap-1.5">
          {props.slides.map((_, dotIndex) => (
            <li
              key={dotIndex}
              className={cn(
                'size-1.5 rounded-full transition-colors',
                dotIndex === index ? 'bg-[#176371]' : 'bg-[#176371]/30',
              )}
            />
          ))}
        </ol>
      )}

      <Dialog.DialogTitle
        className={cn(
          'text-lg font-semibold text-[#333]',
          props.slides.length > 1 ? 'mt-3' : 'mt-4',
        )}
      >
        {withTitleSuffix(props.title, showing.titleSuffix())}
      </Dialog.DialogTitle>

      {props.children}
    </div>
  )
}

/**
 * A config preview image filling the banner. A failed load leaves the slide
 * blank, with no fallback to another image.
 */
function PreviewImage(props: { src: string }) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>(
    'loading',
  )

  return (
    <div className="relative size-full">
      <motion.img
        src={props.src}
        alt=""
        className="size-full object-contain"
        initial={{ opacity: 0 }}
        animate={{ opacity: status === 'loaded' ? 1 : 0 }}
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('failed')}
      />

      <AnimatePresence>
        {status === 'loading' && (
          <motion.div
            className="absolute left-1/2 top-1/2 size-9 -translate-x-1/2 -translate-y-1/2 transform"
            exit={{ opacity: 0 }}
          >
            <Spinner className="size-full" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * The base land, with the reward land revealed over it from the centre on a
 * loop (final-reward-compare.css). Both images are exports of the same
 * 300×200 banner frame with the trees drawn in, so they line up pixel for
 * pixel.
 */
function FinalRewardCompare(props: { baseSrc: string; rewardSrc: string }) {
  const [loadedCount, setLoadedCount] = useState(0)
  // A config missing either URL would leave <img> without a src, which
  // fires neither load nor error — treat it as failed up front.
  const [hasFailed, setHasFailed] = useState(!props.baseSrc || !props.rewardSrc)
  const isLoaded = loadedCount === 2
  const imageHandlers = {
    onLoad: () => setLoadedCount((count) => count + 1),
    onError: () => setHasFailed(true),
  }

  return (
    <div className="relative size-full">
      <motion.div
        className={cn(
          'relative size-full',
          isLoaded && 'final-reward-compare-playing',
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
      >
        <img
          src={props.baseSrc}
          alt=""
          className="absolute inset-0 size-full"
          {...imageHandlers}
        />
        <img
          src={props.rewardSrc}
          alt=""
          className="final-reward-compare-reveal absolute inset-0 size-full"
          {...imageHandlers}
        />
        <div className="final-reward-compare-divider final-reward-compare-divider-left absolute left-[150.08px] top-[58.67px] h-[110px] w-[1.5px]" />
        <div className="final-reward-compare-divider final-reward-compare-divider-right absolute left-[150.08px] top-[58.67px] h-[110px] w-[1.5px]" />
      </motion.div>

      <AnimatePresence>
        {!isLoaded && !hasFailed && (
          <motion.div
            className="absolute left-1/2 top-1/2 size-9 -translate-x-1/2 -translate-y-1/2 transform"
            exit={{ opacity: 0 }}
          >
            <Spinner className="size-full" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
